import { useId } from 'react'
import { useIntl } from 'react-intl'
import InfoTip from '../../../components/novarium/InfoTip'
import { OperatingRoomEvolutionPointDTO } from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { PeakAnalysis } from '../../ServicesStatistics/peaks'
import { pointChipOf } from '../operatingRoomEvolutionModel'
import { formatHoursPlain, formatPercent } from '../operatingRoomFormat'

interface OperatingRoomPeakChipsProps {
  points: OperatingRoomEvolutionPointDTO[]
  granularity: 'DAY' | 'WEEK'
  analysis: PeakAnalysis
  /** Abre el panel del día; `opener` es el chip, a donde vuelve el foco al cerrar el panel. */
  onOpen: (point: OperatingRoomEvolutionPointDTO, opener: HTMLElement) => void
}

/**
 * «Picos»: un botón por cada pico de ocupación marcado en el gráfico («Sábado 19/09 · 92,0 % · 22,1 de 24,0 h ›»), la entrada
 * más fácil con el dedo y con lector de pantalla. Abre el mismo panel que el click en el gráfico. Sin picos no se muestra
 * nada. El ⓘ dice el criterio, con las horas de agenda mínimas que usó el cálculo.
 */
export default function OperatingRoomPeakChips({ points, granularity, analysis, onOpen }: OperatingRoomPeakChipsProps) {
  const intl = useIntl()
  const labelId = useId()
  if (analysis.peaks.length === 0) return null

  return (
    <div className="nv-or-peaks">
      <span className="nv-or-peaks__label">
        <span className="nv-strong" id={labelId}>{intl.formatMessage({ id: 'OperatingRoomIndicatorsPeaksTitle' })}</span>
        <InfoTip
          text={intl.formatMessage({ id: 'OperatingRoomIndicatorsPeaksCriteria' }, { min: formatHoursPlain(intl, analysis.minAttentions) })}
        />
      </span>
      <ul className="nv-or-peaks__list" aria-labelledby={labelId}>
        {analysis.peaks.map((index) => {
          const point = points[index]
          return (
            <li key={point.date}>
              <button type="button" className="nv-or-peak-chip" onClick={(event) => onOpen(point, event.currentTarget)}>
                <span>
                  {intl.formatMessage({ id: 'OperatingRoomIndicatorsPeaksChip' }, {
                    period: pointChipOf(intl, point, granularity),
                    value: formatPercent(intl, point.occupancyPct),
                    hours: intl.formatMessage({ id: 'OperatingRoomIndicatorsOfHours' }, {
                      count: formatHoursPlain(intl, point.insideBlockHours), total: formatHoursPlain(intl, point.capacityHours),
                    }),
                  })}
                </span>
                <i className="pi pi-angle-right" aria-hidden="true" />
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
